import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Grid,
  Stack,
  Table,
  Avatar,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
} from "@mui/material";
import { useQuery } from "@apollo/client";
//Srcs
import "../../Style/pagestyle.scss";
import moment from "moment";
import EmptyData from "../../Components/Include/EmptyData/EmptyData";
import TeacherForm from "../../Components/Teacher/TeacherForm";
import LoadingPage from "../../Utils/LoadingPage";
import TeacherAction from "../../Components/Teacher/TeacherAction";
import FooterPagination from "../../Utils/FooterPagination";

import { AuthContext } from "../../Context/AuthContext";
import { GET_USER_WITH_PAGINATION } from "../../Schema/User";
import { SearchTextField, ButtonCreate } from "../../Utils/Component";
import useWithDimension from "../../Hooks/useWithDimension";

export default function Teacher() {
  // Change Language
  const { t } = useContext(AuthContext);
  const { width } = useWithDimension();

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(8);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [paginationData, setPaginationData] = useState();

  const { refetch } = useQuery(GET_USER_WITH_PAGINATION, {
    variables: {
      role: "Teacher",
      page: page,
      limit: limit,
      keyword: keyword,
    },
    onCompleted: ({ getUserPagination }) => {
      setTableData(getUserPagination?.data);
      setPaginationData(getUserPagination?.paginator);
      setLoading(false);
    },
    onError: (err) => {
      setTableData([]);
      console.log("Error::", err?.message);
    },
  });

  const handleLimit = (e) => {
    setLimit(e.target.value);
    setPage(1);
  };

  const handleSearch = (e) => {
    setPage(1);
    setKeyword(e.target.value);
  };

  useEffect(() => {
    refetch();
  }, [page, keyword]);

  return (
    <div className="page-container">
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={4} lg={3} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("search")}
            </Typography>
            <SearchTextField
              className={"text-field"}
              handleSearch={handleSearch}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={8} lg={9} xl={10}>
            <ButtonCreate
              handleOpen={handleOpen}
              classBtn={"btn-create"}
              classIcon={"icon-add"}
            />

            {open && (
              <TeacherForm
                open={open}
                handleClose={handleClose}
                setRefetch={refetch}
                dialogTitle={"Create"}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      <Box className="body-container">
        <TableContainer sx={{ maxWidth: `${width}px` }}>
          <Table className="table" aria-label="simple table">
            <TableHead className="header-row ">
              <TableRow>
                <TableCell
                  className={t("class-name-header-title-start")}
                  width="5%"
                >
                  {t("no")}
                </TableCell>
                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_id")}
                </TableCell>
                <TableCell className={t("class-name-header-title")}>
                  {t("thead_full_name")}
                </TableCell>

                <TableCell className={t("class-name-header-title")}>
                  {t("thead_gender")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title")}
                  align="center"
                >
                  {t("thead_date_of_birth")}
                </TableCell>
                <TableCell className={t("class-name-header-title")}>
                  {t("thead_email")}
                </TableCell>
                <TableCell className={t("class-name-header-title")}>
                  {t("thead_telephone")}
                </TableCell>
                <TableCell className={t("class-name-header-title")}>
                  {t("thead_address")}
                </TableCell>

                <TableCell
                  className={t("class-name-header-title-end")}
                ></TableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <TableBody className="body">
                <TableRow className="body-row">
                  <TableCell
                    colSpan={10}
                    className="body-cell"
                    sx={{ bgcolor: "#fff" }}
                  >
                    <LoadingPage />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <>
                {tableData?.length === 0 || tableData?.length === undefined ? (
                  <TableBody className="body">
                    <TableRow className="body-row">
                      <TableCell colSpan={10} className="body-cell">
                        <EmptyData />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <>
                    <TableBody className="body">
                      {tableData?.map((row, index) => {
                        return (
                          <TableRow className="body-row" key={index}>
                            <TableCell className="body-cell-start">
                              {index + paginationData?.slNo}
                            </TableCell>
                            <TableCell className="body-cell">
                              {row?.teacherId}
                            </TableCell>
                            <TableCell className="body-cell">
                              <Stack direction="row" spacing={1.4}>
                                <Stack
                                  direction="column"
                                  justifyContent="center"
                                >
                                  <Avatar
                                    sx={{ width: 40, height: 40 }}
                                    alt={row?.userName}
                                    src={row?.profile}
                                  />
                                </Stack>
                                <Stack
                                  direction="column"
                                  justifyContent="center"
                                >
                                  <Typography className="student-name">
                                    {row?.familyName + " " + row?.userName}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </TableCell>
                            <TableCell className="body-cell">
                              {row?.gender === "Male"
                                ? t("thead_male")
                                : t("thead_female")}
                            </TableCell>
                            <TableCell className="body-cell" align="center">
                              {row?.dob &&
                                moment(row?.dob).format("DD-MMM-YYYY")}
                            </TableCell>
                            <TableCell className="body-cell">
                              {row?.email}
                            </TableCell>
                            <TableCell className="body-cell">
                              {row?.phoneNumber}
                            </TableCell>
                            <TableCell className="body-cell">
                              {row?.address}
                            </TableCell>
                            <TableCell className="body-cell-end" align="right">
                              <TeacherAction
                                editData={row}
                                setRefetch={refetch}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </>
                )}
              </>
            )}
          </Table>
        </TableContainer>

        {/* ==================== Pagination  ============================= */}
        <FooterPagination
          totalPages={paginationData?.totalPages}
          totalDocs={paginationData?.totalDocs}
          limit={limit}
          page={page}
          setPage={setPage}
          handleLimit={handleLimit}
          setLoading={setLoading}
        />
      </Box>
    </div>
  );
}
